import { useRef, useState } from 'react';
import type { InstallView } from '@dunea/shared';
import { apiCreatePanelSession } from '~/lib/api/marketplace';
import { LoadError, Spinner } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { ListingIcon } from './parts';
import { panelFrameSrc } from './marketplace-model';
import { PLUGIN_FRAME_SANDBOX, usePluginFrameMessages } from './use-plugin-frame-messages';

/**
 * A plugin's own workbench panel, in a sandboxed iframe on the plugin's origin
 * (PLUGIN_FRAME_SANDBOX). Only messages whose `event.source` is this iframe
 * are acted on (usePluginFrameMessages).
 */
export function PluginPanelFrame({
  appId,
  install,
  onClose,
}: {
  appId: string;
  install: InstallView;
  onClose: () => void;
}) {
  const session = useResource(() => apiCreatePanelSession(appId, install.id), `${appId}:${install.id}`);
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<number | null>(null);
  const src = session.data ? panelFrameSrc(session.data.url, window.location.origin) : null;

  usePluginFrameMessages(frame, { name: install.name, onResize: setHeight, onClose });

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-surface" role="dialog" aria-label={`${install.name} panel`}>
      <div className="flex items-center gap-2 border-b border-hairline px-4 py-3">
        <button type="button" onClick={onClose} className="ui-btn-icon" aria-label="Close the plugin panel">
          <div className="i-ph:arrow-left text-[14px]" />
        </button>
        <ListingIcon url={install.iconUrl} name={install.name} size={22} />
        <h3 className="min-w-0 truncate text-[13px] font-medium text-primary">{install.name}</h3>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => void session.reload()}
          className="ui-btn-icon"
          title="Reload panel"
          aria-label="Reload panel"
        >
          <div className="i-ph:arrow-clockwise text-[13px]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {session.error ? (
          <div className="p-4">
            <LoadError message="Could not open this plugin's panel." onRetry={() => void session.reload()} />
          </div>
        ) : session.loading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner className="text-[16px] text-muted" />
          </div>
        ) : !src ? (
          <div className="p-4 text-[12px] text-danger">This plugin's panel URL isn't allowed (it must be https).</div>
        ) : (
          <iframe
            ref={frame}
            key={src}
            src={src}
            title={`${install.name} panel`}
            sandbox={PLUGIN_FRAME_SANDBOX}
            referrerPolicy="no-referrer"
            allow="clipboard-write"
            className="block w-full border-0 bg-white"
            style={{ height: height ? `${height}px` : '100%', minHeight: '100%' }}
          />
        )}
      </div>
    </div>
  );
}
